import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

interface HelpTooltipProps {
  text: string;
  children: React.ReactNode;
  width?: number;
}

interface TooltipPosition {
  top: number;
  left: number;
  placement: "top" | "bottom";
}

const TOOLTIP_Z_INDEX = 110;
const VIEWPORT_PADDING = 8;
const GAP = 8;

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

export const HelpTooltip: React.FC<HelpTooltipProps> = ({
  text,
  children,
  width = 224,
}) => {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState<TooltipPosition>({
    top: 0,
    left: 0,
    placement: "top",
  });

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const halfWidth = width / 2;
    const left = clamp(
      rect.left + rect.width / 2,
      VIEWPORT_PADDING + halfWidth,
      window.innerWidth - VIEWPORT_PADDING - halfWidth
    );

    const spaceAbove = rect.top;
    const spaceBelow = window.innerHeight - rect.bottom;
    const placement = spaceAbove >= spaceBelow ? "top" : "bottom";
    const top =
      placement === "top" ? rect.top - GAP : rect.bottom + GAP;

    setPosition({ top, left, placement });
  }, [width]);

  const show = () => {
    updatePosition();
    setVisible(true);
  };

  const hide = () => setVisible(false);

  useLayoutEffect(() => {
    if (!visible) return;

    updatePosition();

    const handleReposition = () => updatePosition();
    window.addEventListener("scroll", handleReposition, true);
    window.addEventListener("resize", handleReposition);

    return () => {
      window.removeEventListener("scroll", handleReposition, true);
      window.removeEventListener("resize", handleReposition);
    };
  }, [visible, updatePosition]);

  const transform =
    position.placement === "top"
      ? "translate(-50%, -100%)"
      : "translate(-50%, 0)";

  return (
    <>
      <span
        ref={triggerRef}
        className="inline-flex"
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
      >
        {children}
      </span>
      {visible &&
        createPortal(
          <div
            role="tooltip"
            className="fixed rounded-lg border border-border bg-secondary px-3 py-2 text-[11px] leading-snug text-text-secondary shadow-lg pointer-events-none"
            style={{
              top: position.top,
              left: position.left,
              width,
              transform,
              zIndex: TOOLTIP_Z_INDEX,
            }}
          >
            {text}
          </div>,
          document.body
        )}
    </>
  );
};
